import {
  getAgentToolIds,
  type Agent,
  type GuardrailConfig,
  type GuardrailOutputStrategy,
  type IUpdateAgentAIConfigurationPayload,
} from 'features/chat/agent-chat-module/types/agent.service.type';
import {
  computeDefaultBasePrompt,
  isManagedDefaultPrompt,
  type AgentCapabilitiesState,
} from 'utils/agent-default-prompt';
import { isConfigSectionComingSoon } from '../components/agent-configuration.constants';
import { canManageKb, canManageSkills, canManageSubagents, canManageTools } from './agent-share-access';

/** The values the configuration page edits and sends to update-ai-configurations. */
export type AgentConfigValues = {
  sectionState: Record<string, boolean>;
  llmModel: string;
  llmProvider: string;
  llmModelId: string;
  isBlocksProvider: boolean;
  responseType: string;
  responseFormat: string | null;
  basePrompt: string;
  temperature: number;
  maxTokens: number;
  enableSummary: boolean;
  lastNConversations: number;
  enableSmartSuggestions: boolean;
  enableQueryEnhancement: boolean;
  scoreThreshold: number;
  topK: number;
  denseWeight: number;
  guardrailOutputStrategy: GuardrailOutputStrategy;
  configuredGuardrails: Record<string, GuardrailConfig>;
  folderIds: string[];
  toolIds: string[];
  skillIds: string[];
  subagentIds: string[];
};

export const capabilitiesFromSectionState = (
  state: Record<string, boolean>
): AgentCapabilitiesState => ({
  enableTools: !!state.tools,
  enableSkills: !!state.skills,
  enableSubagents: !!state.subagents,
  enableKnowledgeBase: !!state['knowledge-base'],
});

/**
 * Empty or still-managed-default prompts auto-fill from current capabilities;
 * a custom, user-authored prompt is left untouched.
 */
export const resolveInitialBasePrompt = (
  storedBasePrompt: string,
  capabilities: AgentCapabilitiesState
) =>
  !storedBasePrompt.trim() || isManagedDefaultPrompt(storedBasePrompt)
    ? computeDefaultBasePrompt(capabilities)
    : storedBasePrompt;

/** The agent's saved configuration, with the defaults the configuration page starts from. */
export function getAgentConfigValues(agent: Agent): AgentConfigValues {
  const sectionState = {
    'knowledge-base': agent.enable_knowledge_base ?? agent.enable_rag ?? true,
    tools: agent.enable_tools ?? false,
    skills: agent.enable_skills ?? false,
    subagents: agent.enable_subagents ?? false,
    memory: !!agent.enable_memory,
    'human-handoff': isConfigSectionComingSoon('human-handoff')
      ? false
      : (agent.enable_human_handoff ?? false),
    guardrails:
      agent.enable_guardrails ??
      ((agent.guardrail?.enable_pre_validation ?? false) ||
        (agent.guardrail?.enable_post_validation ?? false)),
  };

  return {
    sectionState,
    llmModel: agent.llm_config?.model_name || '',
    llmProvider: agent.llm_config?.provider || '',
    llmModelId: agent.llm_config?.model_id || '',
    isBlocksProvider: agent.llm_config?.is_blocks_provider ?? true,
    responseType: agent.response_type || 'text',
    responseFormat: agent.response_format ?? null,
    basePrompt: resolveInitialBasePrompt(
      agent.llm_config?.base_prompt ?? '',
      capabilitiesFromSectionState(sectionState)
    ),
    temperature: Math.min(agent.llm_config?.temperature ?? 0.7, 1),
    maxTokens: Math.min(agent.llm_config?.max_tokens ?? 10000, 10000),
    enableSummary: agent.memory_config?.enable_summary ?? true,
    lastNConversations: agent.memory_config?.last_n_conversations ?? 5,
    enableSmartSuggestions: agent.enable_questions_suggestions ?? true,
    enableQueryEnhancement: agent.rag_config?.enable_query_enhancement ?? true,
    scoreThreshold: agent.rag_config?.score_threshold ?? 0.7,
    topK: agent.rag_config?.top_k ?? 10,
    denseWeight: agent.rag_config?.dense_weight ?? 0.9,
    guardrailOutputStrategy: agent.guardrail_output_strategy ?? 'block',
    configuredGuardrails: agent.guardrails ?? {},
    folderIds: agent.folder_ids ?? [],
    toolIds: getAgentToolIds(agent),
    skillIds: agent.skill_ids ?? [],
    subagentIds: agent.subagent_ids ?? [],
  };
}

/** Payload for update-ai-configurations, sent by the configuration page and the chat settings drawer. */
export function buildAgentConfigPayload(
  agent: Agent,
  projectKey: string,
  values: AgentConfigValues,
  userId?: string | null
): IUpdateAgentAIConfigurationPayload {
  const { sectionState } = values;
  const payload: IUpdateAgentAIConfigurationPayload = {
    agent_id: agent.id,
    project_key: projectKey,
    name: agent.name,
    description: agent.description,
    enable_guardrails: !!sectionState.guardrails,
    enable_memory: !!sectionState.memory,
    enable_human_handoff: isConfigSectionComingSoon('human-handoff')
      ? false
      : !!sectionState['human-handoff'],
    response_type: values.responseType,
    response_format: values.responseFormat,
    llm_config: {
      ...agent.llm_config,
      provider: values.llmProvider || agent.llm_config.provider,
      model_name: values.llmModel || agent.llm_config.model_name,
      is_blocks_provider: values.isBlocksProvider,
      model_id: values.llmModelId || '',
      base_prompt: values.basePrompt,
      temperature: values.temperature,
      max_tokens: values.maxTokens,
    },
    memory_config: {
      ...agent.memory_config,
      enable_summary: values.enableSummary,
      last_n_conversations: values.lastNConversations,
    },
    enable_questions_suggestions: values.enableSmartSuggestions,
    rag_config: {
      ...agent.rag_config,
      enable_query_enhancement: values.enableQueryEnhancement,
      score_threshold: values.scoreThreshold,
      top_k: values.topK,
      dense_weight: values.denseWeight,
      keyword_weight: Number((1 - values.denseWeight).toFixed(2)),
    },
    guardrail_output_strategy: values.guardrailOutputStrategy,
    guardrails: Object.entries(values.configuredGuardrails).reduce(
      (acc, [key, val]) => {
        const cleaned: GuardrailConfig = {
          enabled: val?.enabled ?? true,
        };
        if (Array.isArray(val?.keywords)) {
          cleaned.keywords = val.keywords.map((kw: string) => kw.trim()).filter(Boolean);
        }
        acc[key] = cleaned;
        return acc;
      },
      {} as Record<string, GuardrailConfig>
    ),
  };

  // Only send manage* fields the caller may change — BE returns 403 otherwise.
  if (canManageKb(agent, userId)) {
    payload.enable_rag = !!sectionState['knowledge-base'];
    payload.enable_knowledge_base = !!sectionState['knowledge-base'];
    payload.kb_ids = agent.kb_ids ?? [];
    payload.folder_ids = values.folderIds;
  }
  if (canManageTools(agent, userId)) {
    payload.enable_tools = !!sectionState.tools;
    payload.tool_ids = values.toolIds;
  }
  if (canManageSkills(agent, userId)) {
    payload.enable_skills = !!sectionState.skills;
    payload.skill_ids = values.skillIds;
  }
  if (canManageSubagents(agent, userId)) {
    payload.enable_subagents = !!sectionState.subagents;
    payload.subagent_ids = values.subagentIds;
  }

  return payload;
}
